import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle2, XCircle, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Requirement = ({ met, text }) => (
  <li className={`flex items-center gap-2 text-sm transition-colors ${met ? 'text-green-600' : 'text-slate-400'}`}>
    {met ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <XCircle className="h-4 w-4 shrink-0" />}
    {text}
  </li>
);

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mismatch, setMismatch] = useState(false);

  // Real-time validation
  const hasMinLength = password.length >= 8;
  const hasUppercase = /[A-Z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const allReqsMet = hasMinLength && hasUppercase && hasNumber;

  useEffect(() => {
    if (!token) {
      toast.error('Enlace de restablecimiento inválido');
      navigate('/forgot-password');
    }
  }, [token, navigate]);

  useEffect(() => {
    if (confirm && password !== confirm) {
      setMismatch(true);
    } else {
      setMismatch(false);
    }
  }, [password, confirm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allReqsMet) return;
    if (password !== confirm) {
      setMismatch(true);
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/reset-password`, {
        token,
        new_password: password
      });
      toast.success('Contraseña actualizada correctamente', {
        description: 'Ya puedes iniciar sesión con tu nueva contraseña.'
      });
      navigate('/login');
    } catch (error) {
      const msg = error.response?.data?.detail || 'El enlace ha expirado o es inválido.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="mb-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-6">
              <img
                src="https://customer-assets.emergentagent.com/job_admin-central-39/artifacts/g46w9dxw_Favicon.png"
                alt="LivCom"
                className="h-10 w-10"
              />
              <span className="text-3xl font-heading font-bold text-primary">LivCom</span>
            </div>
            <div className="h-14 w-14 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShieldCheck className="h-7 w-7 text-accent" />
            </div>
            <h1 className="text-2xl font-heading font-semibold text-slate-900 mb-2">
              Restablecer contraseña
            </h1>
            <p className="text-slate-500">
              Crea una nueva contraseña segura para tu cuenta.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
            {/* New Password */}
            <div>
              <Label htmlFor="password" className="text-slate-700 font-medium">Nueva contraseña</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="password-input"
                  className="h-11 bg-slate-50 border-slate-200 focus:bg-white pr-10 transition-colors"
                  placeholder="Mínimo 8 caracteres"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-slate-400 hover:text-slate-600 transition-colors"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>

              {/* Requirements */}
              {password.length > 0 && (
                <ul className="mt-3 space-y-1 pl-1" data-testid="password-requirements">
                  <Requirement met={hasMinLength} text="Mínimo 8 caracteres" />
                  <Requirement met={hasUppercase} text="Al menos 1 letra mayúscula" />
                  <Requirement met={hasNumber} text="Al menos 1 número" />
                </ul>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <Label htmlFor="confirm" className="text-slate-700 font-medium">Confirmar contraseña</Label>
              <div className="relative mt-1">
                <Input
                  id="confirm"
                  type={showConfirm ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  data-testid="confirm-password-input"
                  className={`h-11 bg-slate-50 border-slate-200 focus:bg-white pr-10 transition-colors ${
                    mismatch ? 'border-destructive focus:border-destructive' : ''
                  }`}
                  placeholder="Repite tu contraseña"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-3 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showConfirm ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {mismatch && (
                <p className="text-sm text-destructive mt-1" data-testid="mismatch-error">
                  Las contraseñas no coinciden
                </p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full bg-accent hover:bg-accent/90 text-white rounded-full py-6 font-semibold shadow-lg"
              disabled={loading || !allReqsMet || mismatch || !confirm}
              data-testid="submit-button"
            >
              {loading ? 'Guardando...' : 'Guardar nueva contraseña'}
            </Button>
          </form>

          <Link
            to="/login"
            className="block text-center text-text-secondary mt-6 hover:text-primary transition-colors"
            data-testid="back-to-login-link"
          >
            ← Volver al login
          </Link>
        </div>
      </div>

      {/* Right - Decorative */}
      <div className="hidden lg:block flex-1 bg-gradient-to-br from-primary to-primary/90 relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-white text-center space-y-4">
            <h2 className="text-4xl font-heading font-bold">
              Contraseña segura
            </h2>
            <p className="text-xl text-white/80">
              Usa letras mayúsculas, números y mínimo 8 caracteres para mayor seguridad.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
